import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useEffect, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';

import {
	ComposerAttachments,
	NO_ATTACHABLE,
	totalAttachmentBytes,
	type ComposerAttachmentItem,
} from '@/components/composer/composer-attachments';
import { previousLetterNote } from '@/components/inbox/auto-reply-notice';
import { ActivityIndicator, Button, ComposerWindow, Text, TextInput, useTheme } from '@/components/ui';
import { useComposerDraft } from '@/hooks/use-composer-draft';
import { useNeedsInfoPreview, useSendNeedsInfoEmail } from '@/hooks/use-data';
import type { NeedsInfoSendResult } from '@/services/data-service';
import { REVIEW_ACCENT } from '@/styles/theme';

/**
 * A letter to the homeowner about this request, before it goes out (#369).
 *
 * The load-bearing rule: a gated skip must NEVER read as a success. It is the normal
 * outcome in every environment until someone flips the org switch, so the banner says so
 * while the reviewer is still writing, and the outcome dialog distinguishes "sent" from
 * "recorded, not emailed" rather than showing one cheerful toast for both.
 *
 * ── IT IS NO LONGER NEEDS-INFO-ONLY ──
 *
 * This began as the "your submission is missing X" composer and refused to open usefully
 * unless there were unmet REQUIRED items, a `needs_info` template existed, and the status
 * could legally move to Needs Additional Info. That made whole categories of request
 * unwritable: a request type with no configured requirements can never have anything
 * missing, so it could never be followed up on, and an Approved request could not be sent
 * so much as a note.
 *
 * Now the only thing that stops a send is having no address to send to. Everything else is
 * a NOTE (muted, informational), the wording falls back to a built-in follow-up letter, and
 * the status move is an explicit opt-in rather than a side effect of writing to someone.
 */


export interface NeedsInfoModalProps {
	applicationId: string;
	visible: boolean;
	onDismiss: () => void;
	/** Fired after a successful send so the parent can report the outcome. */
	onSent: (result: NeedsInfoSendResult) => void;
	/** Open collapsed — used when `ComposerHost` restores a draft after a page reload. */
	startMinimized?: boolean;
	/** Bumped by the host to bring an already-open, collapsed composer back to the front. */
	expandRequest?: number;
	/** Lets the host warn before a click replaces or closes work in progress. */
	onDirtyChange?: (dirty: boolean) => void;
	/** The signed-in user id, from `ComposerHost`. Absent means do not persist a draft. */
	draftScope?: string | null;
}

/**
 * Only the reviewer's own words and their explicit choice.
 *
 * `expectedMissingKeys` is deliberately NOT persisted: it is the server's view of what is
 * still outstanding, and it exists to REFUSE a send whose checklist moved underneath it. A
 * stale copy restored from yesterday would defeat the very guard it implements.
 */
interface NeedsInfoDraft {
	subject: string;
	body: string;
	updateStatus: boolean | null;
}

export const NeedsInfoModal: React.FC<NeedsInfoModalProps> = ({
	applicationId, visible, onDismiss, onSent, startMinimized, expandRequest, onDirtyChange, draftScope,
}) => {
	const theme = useTheme();
	const { data: preview, isLoading, error } = useNeedsInfoPreview(applicationId, { enabled: visible });
	const send = useSendNeedsInfoEmail();

	const [subject, setSubject] = useState('');
	const [body, setBody] = useState('');
	const [dirty, setDirty] = useState(false);
	/**
	 * Whether to also move the request to Needs Additional Info.
	 *
	 * `null` means UNTOUCHED — the server's default applies, read live at every use site as
	 * `updateStatus ?? preview.statusChange.willChange`. A boolean means the reviewer decided,
	 * and their choice then survives anything the server says next.
	 *
	 * ── WHY THE RE-SEED EFFECT MUST NOT WRITE THIS ──
	 *
	 * It used to, and that silently reversed an explicit decision. `useNeedsInfoPreview` runs
	 * with `staleTime: 0` and TanStack's default `refetchOnWindowFocus`, so merely clicking
	 * back into the tab produces a fresh `preview`. Toggling this checkbox does not set
	 * `dirty` (that flag is about the TEXT), so the effect re-ran and overwrote the reviewer's
	 * "no" with the server default "yes" — and the send then moved a request to Needs
	 * Additional Info that nobody asked to move.
	 *
	 * Seeding was redundant anyway: the `?? ` fallback below already supplies the default.
	 * Leaving it `null` is what makes an untouched checkbox keep tracking the server — so if a
	 * refetch shows the checklist was completed elsewhere, the default correctly flips off.
	 */
	const [updateStatus, setUpdateStatus] = useState<boolean | null>(null);
	/** Files on the letter (#606): the request's own, uploads, and Dropbox picks. */
	const [attachments, setAttachments] = useState<ComposerAttachmentItem[]>([]);
	const [attachmentsBusy, setAttachmentsBusy] = useState(false);
	/**
	 * Files were added or removed. Its OWN flag, not `dirty`: `dirty` also freezes the letter text
	 * against refetches, and attaching a file must not make the reviewer's words look edited. But
	 * it IS unsaved work — closing would throw the staged files away — so the host must ask.
	 */
	const [attachmentsDirty, setAttachmentsDirty] = useState(false);
	/** This composer has no parent error channel, so attachment failures are said inline. */
	const [attachError, setAttachError] = useState<string | null>(null);

	// Re-seed whenever a fresh preview arrives, but never clobber edits in progress —
	// the preview refetches on focus and would otherwise wipe what someone just typed.
	useEffect(() => {
		if (!preview || dirty) return;
		setSubject(preview.subject);
		setBody(preview.bodyText);
	}, [preview, dirty]);

	useEffect(() => {
		if (!visible) {
			setDirty(false);
			setUpdateStatus(null);
			setAttachments([]);
			setAttachmentsDirty(false);
			setAttachError(null);
			restoredRef.current = null;
		}
	}, [visible]);

	/**
	 * Restore whatever was typed before the page was reloaded.
	 *
	 * Order against the re-seed effect above does not matter: if this runs first it sets
	 * `dirty` and the seed skips; if the seed runs first this overwrites it. Either way the
	 * reviewer's own words beat the server's template.
	 */
	const draft = useComposerDraft<NeedsInfoDraft>(draftScope, visible ? `needs-info:${applicationId}` : null);
	const restoredRef = useRef<string | null>(null);
	useEffect(() => {
		if (!visible || !draft.ready || !draft.restored) return;
		if (restoredRef.current === applicationId) return;
		restoredRef.current = applicationId;
		setSubject(draft.restored.subject);
		setBody(draft.restored.body);
		setUpdateStatus(draft.restored.updateStatus);
		setDirty(true);
	}, [visible, applicationId, draft.ready, draft.restored]);

	// Only once the reviewer has actually edited the text — otherwise merely opening the
	// composer would store the server's own template as a "draft".
	useEffect(() => {
		if (!visible || !dirty) return;
		draft.save({ subject, body, updateStatus });
	}, [visible, dirty, subject, body, updateStatus, draft]);

	// Same signal the host uses to warn before a click throws this away.
	// A file still being staged is unsaved work too: closing mid-upload would drop it without a word.
	useEffect(() => {
		onDirtyChange?.(visible && (dirty || attachmentsDirty || attachmentsBusy));
	}, [visible, dirty, attachmentsDirty, attachmentsBusy, onDirtyChange]);

	const gate = preview?.emailGate;
	const willSend = gate?.willActuallySend ?? false;
	const busy = send.isPending;

	/**
	 * Every condition here mirrors the Send button's `disabled` prop, and that is not
	 * belt-and-braces: Cmd+Enter reaches this directly, so `disabled` guards nothing. Without
	 * the re-check, two presses send the letter twice AND apply the status move twice, an
	 * empty body emails a blank letter, and a request with no submitter email posts anyway.
	 */
	const attachable = preview?.attachable ?? NO_ATTACHABLE;
	const attachmentsFit = !attachments.length || totalAttachmentBytes(attachments) <= attachable.maxBytes;
	const canSubmit = !!preview && !busy && !attachmentsBusy && attachmentsFit && preview.canSend
		&& !!subject.trim() && !!body.trim();

	const submit = async () => {
		if (!canSubmit || !preview) return;
		try {
			const result = await send.mutateAsync({
				id: applicationId,
				body: {
					subject: subject.trim(),
					bodyText: body.trim(),
					// Refuses the send if someone ticked the last item while this was open.
					expectedMissingKeys: preview.missingKeys,
					// Explicit, so the server does not re-derive a default that may now
					// disagree with the checkbox the reviewer is looking at.
					updateStatus: updateStatus ?? preview.statusChange.willChange,
					attachments: attachments.map((item) => item.ref),
				},
			});
			// The letter is on the request now; a stored draft would resurrect it next visit.
			draft.clear();
			onSent(result);
			onDismiss();
		} catch {
			// Left open with the text intact — the error surfaces through the parent's
			// notification, and retyping a letter because of a 409 is miserable.
		}
	};

	/** Pinned to the window's bottom edge, so Send survives any amount of scrolling. */
	const actions = preview && !isLoading && !error ? (
		<View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8 }}>
			<Button mode="outlined" onPress={onDismiss} disabled={busy}>Cancel</Button>
			<Button
				mode="contained"
				onPress={submit}
				loading={busy}
				disabled={!canSubmit}
				testID="btn-send-needs-info"
			>
				{willSend ? 'Send' : 'Record without emailing'}
			</Button>
		</View>
	) : undefined;

	return (
		/*
			Titled for what it now does. It began as "Request more information" and only
			worked when something was outstanding; it sends any letter to the homeowner
			about this request, so a title naming the narrower case was misleading on the
			majority of requests.
		*/
		<ComposerWindow
			visible={visible}
			onDismiss={onDismiss}
			onSubmit={submit}
			busy={busy}
			startMinimized={startMinimized}
			expandRequest={expandRequest}
			title="Email the homeowner"
			subtitle={preview?.missing.length
				? 'The homeowner is asked to reply to this email with what is missing.'
				: 'Replies come back onto this request.'}
			footer={actions}
			testID="needs-info-composer"
		>
			{isLoading ? (
				<View style={{ padding: 32, alignItems: 'center' }}><ActivityIndicator /></View>
			) : error || !preview ? (
				<Text style={{ color: theme.colors.error }}>Could not compose the letter.</Text>
			) : (
				<>
						{/*
							Stated BEFORE sending, not after. "Recorded but not emailed" is the
							normal outcome until go-live, and someone who only learns that from a
							toast has already assumed the homeowner was told.
						*/}
						{!willSend ? (
							<View
								testID="needs-info-gate-banner"
								style={{
									flexDirection: 'row', alignItems: 'flex-start',
									backgroundColor: theme.colors.errorContainer,
									borderRadius: 8, padding: 10, marginBottom: 12,
								}}
							>
								<Ionicons name="warning" size={16} color={REVIEW_ACCENT} style={{ marginRight: 8, marginTop: 1 }} />
								<Text style={{ flex: 1, minWidth: 0, fontSize: 12, color: theme.colors.onErrorContainer }}>
									This will be recorded on the request but NOT emailed. {gate?.reason}
								</Text>
							</View>
						) : null}

						{preview.blockers.length ? (
							<View testID="needs-info-blockers" style={{ marginBottom: 12 }}>
								{preview.blockers.map((blocker) => (
									<Text key={blocker} style={{ color: theme.colors.error, fontSize: 12 }}>• {blocker}</Text>
								))}
							</View>
						) : null}

						{/*
							MUTED, not red. These are the conditions that used to refuse the send
							outright — nothing outstanding, no template, an unavailable status move.
							They no longer stop anything, so colouring them like errors would keep
							telling reviewers something is wrong when nothing is.
						*/}
						{preview.notes?.length ? (
							<View testID="needs-info-notes" style={{ marginBottom: 12 }}>
								{preview.notes.map((note) => (
									<View key={note} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 2 }}>
										<Ionicons
											name="information-circle-outline"
											size={14}
											color={theme.colors.onSurfaceVariant}
											style={{ marginRight: 6, marginTop: 1 }}
										/>
										<Text style={{ flex: 1, minWidth: 0, color: theme.colors.onSurfaceVariant, fontSize: 12 }}>
											{note}
										</Text>
									</View>
								))}
							</View>
						) : null}

						{preview.alreadySentAt ? (
							// Informational rather than a warning: several follow-ups on one request
							// is now expected, so "a letter like this already went out" would cry
							// wolf every time after the first.
							<Text style={{ color: theme.colors.onSurfaceVariant, fontSize: 12, marginBottom: 12 }} testID="needs-info-already-sent">
								{previousLetterNote('You last wrote to this homeowner', preview.alreadySentAt, preview.alreadySentDelivery)}
							</Text>
						) : null}

						<View style={{ marginBottom: 10 }}>
								<Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant }}>
									To: {preview.to ?? '— no submitter email —'}
								</Text>
								<Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant }}>
									From: {preview.from.name} &lt;{preview.from.email}&gt;
								</Text>
								<Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant }}>
									Replies go to: {preview.replyTo}
								</Text>
							</View>

							{preview.missing.length ? (
								<View style={{ marginBottom: 12 }}>
									<Text style={{ fontWeight: '700', fontSize: 12, marginBottom: 4 }}>
										Outstanding ({preview.missing.length})
									</Text>
									<View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
										{preview.missing.map((item) => (
											<View
												key={item.key}
												style={{
													borderWidth: 1, borderRadius: 12, paddingHorizontal: 8, paddingVertical: 3,
													borderColor: theme.colors.outlineVariant ?? theme.colors.outline,
												}}
											>
												<Text style={{ fontSize: 11 }}>{item.label}</Text>
											</View>
										))}
									</View>
								</View>
							) : null}

							{preview.templateWarnings.map((warning) => (
								<Text key={warning} style={{ color: REVIEW_ACCENT, fontSize: 12, marginBottom: 6 }}>
									{warning}
								</Text>
							))}

							<Text style={{ fontWeight: '700', marginTop: 6, marginBottom: 4 }}>Subject</Text>
							<TextInput
								value={subject}
								onChangeText={(v) => { setSubject(v); setDirty(true); }}
								testID="needs-info-subject"
							/>

							<Text style={{ fontWeight: '700', marginTop: 12, marginBottom: 4 }}>Message</Text>
							<TextInput
								value={body}
								onChangeText={(v) => { setBody(v); setDirty(true); }}
								multiline
								numberOfLines={12}
								style={{ minHeight: 200 }}
								testID="needs-info-body"
							/>

							<ComposerAttachments
								target={{ kind: 'application', id: applicationId }}
								attachable={attachable}
								items={attachments}
								onChange={(update) => { setAttachments(update); setAttachmentsDirty(true); setAttachError(null); }}
								onError={(e, fallback) => setAttachError(e instanceof Error && e.message ? e.message : fallback)}
								onBusyChange={setAttachmentsBusy}
								disabled={busy}
							/>
							{attachError ? (
								<Text testID="needs-info-attach-error" style={{ color: theme.colors.error, fontSize: 12, marginTop: 4 }}>
									{attachError}
								</Text>
							) : null}

							{/*
								THREE cases, and only two of them are a template.

								`builtin` has two quite different causes. With items outstanding it
								means no needs-info template exists, and creating one WILL be used, so
								offering the link is honest. With nothing outstanding the built-in
								follow-up wording is used deliberately — even when a perfectly good
								needs-info template exists — because that template announces missing
								requirements and then lists none. Inviting someone to "set up a
								template for this letter" there promises a customisation that can
								never take effect, so it says so instead. A dedicated follow-up
								template kind is what would change this.
							*/}
							{preview.templateSource === 'builtin' && !preview.missing.length ? (
								<Text
									testID="needs-info-builtin-wording"
									style={{
										color: theme.colors.onSurfaceVariant, fontSize: 12,
										paddingVertical: 8, paddingHorizontal: 4,
									}}
								>
									This general follow-up uses built-in wording — edit it above before sending.
								</Text>
							) : (
								<Pressable
									onPress={() => router.push('/settings/email-templates')}
									testID="btn-edit-needs-info-template"
									style={{ alignSelf: 'flex-start', paddingVertical: 8, paddingHorizontal: 4 }}
								>
									<Text style={{ color: theme.colors.primary, fontSize: 12, fontWeight: '600' }}>
										{preview.templateSource === 'builtin'
											? 'Set up a template for this letter'
											: 'Edit the template this came from'}
									</Text>
								</Pressable>
							)}

						{/*
							Only when the move is legal at all. On an Approved or Rejected request
							there is no such transition, so offering a checkbox that 409s would be
							worse than not offering it.
						*/}
						{preview.statusChange.allowed
							&& preview.statusChange.from !== preview.statusChange.to ? (
								<Pressable
									onPress={() => setUpdateStatus(!(updateStatus ?? preview.statusChange.willChange))}
									testID="toggle-needs-info-update-status"
									style={{ flexDirection: 'row', alignItems: 'center', marginTop: 12 }}
								>
									<Ionicons
										name={(updateStatus ?? preview.statusChange.willChange) ? 'checkbox' : 'square-outline'}
										size={20}
										color={(updateStatus ?? preview.statusChange.willChange)
											? theme.colors.primary
											: theme.colors.onSurfaceVariant}
										style={{ marginRight: 8 }}
									/>
									<Text style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
										Also move this request to Needs Additional Info
									</Text>
								</Pressable>
							) : null}
					</>
			)}
		</ComposerWindow>
	);
};

export default NeedsInfoModal;
